<script>
import { toMoney, toPercent, getNumber } from '@utils/Format'
import { customSort as baseCustomSort } from '@utils/Sort'
import { GlobalBus } from '@services/bus'
import { MODAL } from '@constants/Text'
import ClientTags from '@modules/crm/components/client-tags/client-tags.vue'
import CommentDisplay from '@modules/portfolio-review/components/comment-display/comment-display.vue'
import TableRowActions from '@modules/consolidator/components/table-row-actions/table-row-actions.vue'
import { RECOMENDACAO_COLORS, RECOMENDACAO_LABELS } from '@modules/consolidator/constants/recomendacao'

const ONLY_NOTES_TOOLTIP = 'Disponível somente na análise da simulação'

export default {
  name: 'SimulatorPortfolioTable',
  components: {
    ClientTags,
    CommentDisplay,
    TableRowActions,
  },
  props: {
    headers: {
      type: Array,
      default: () => [],
    },
    items: {
      type: Array,
      default: () => [],
    },
    groupBy: {
      type: [String, Array],
      default: null,
    },
    fixedAssets: {
      type: Array,
      default: () => [],
    },
    onlyEditNotes: {
      type: Boolean,
      default: false,
    },
    maxTagsDisplay: {
      type: Number,
      default: null,
    },
    isCompact: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      compactHiddenFields: ['recomendacao', 'setor', 'comentario', 'tags', 'actions'],
      recomendacaoColors: RECOMENDACAO_COLORS,
      recomendacaoLabels: RECOMENDACAO_LABELS,
    }
  },
  computed: {
    visibleHeaders() {
      const headerList = Array.isArray(this.headers) ? this.headers : []
      if (!this.isCompact) {
        return headerList
      }
      return headerList.filter((headerItem) => {
        const headerKey = headerItem && headerItem.value
        return !this.compactHiddenFields.includes(headerKey)
      })
    },
    tableRows() {
      const itemList = Array.isArray(this.items) ? this.items : []
      return itemList.map((assetItem) => ({
        ...assetItem,
        actions: this.buildRowActions(assetItem),
      }))
    },
  },
  methods: {
    customSort(itemList, sortKey, sortDescList) {
      const sortDict = {
        texts: ['nome_ativo', 'referencia'],
        numbers: ['pl_fixado', 'aplicacao', 'Aplicação', 'percent'],
        dates: [],
      }
      return baseCustomSort(itemList, sortKey, sortDescList, sortDict)
    },
    sumPl(groupItems) {
      const validItems = Array.isArray(groupItems) ? groupItems : []
      const totalValue = validItems.reduce((currentTotal, assetItem) => {
        const rawValue = assetItem && assetItem.Aplicação
        return currentTotal + getNumber(rawValue)
      }, 0)
      return toMoney(totalValue)
    },
    sumPercent(groupItems) {
      const validItems = Array.isArray(groupItems) ? groupItems : []
      const totalValue = validItems.reduce((currentTotal, assetItem) => {
        const rawValue = assetItem && assetItem.percent
        return currentTotal + getNumber(rawValue)
      }, 0)
      return toPercent(totalValue)
    },
    formatReference(assetItem) {
      if (!assetItem) {
        return ''
      }
      return assetItem.CNPJ || assetItem.referencia || ''
    },
    getVisibleTags(assetItem) {
      const tagsList = this.getTags(assetItem)
      if (!this.maxTagsDisplay || tagsList.length <= this.maxTagsDisplay) {
        return tagsList
      }
      return tagsList.slice(0, this.maxTagsDisplay)
    },
    getHiddenCount(assetItem) {
      const tagsList = this.getTags(assetItem)
      if (this.maxTagsDisplay && tagsList.length > this.maxTagsDisplay) {
        return tagsList.length - this.maxTagsDisplay
      }
      return 0
    },
    getHiddenTags(assetItem) {
      const tagsList = this.getTags(assetItem)
      if (!this.maxTagsDisplay || tagsList.length <= this.maxTagsDisplay) {
        return []
      }
      return tagsList.slice(this.maxTagsDisplay)
    },
    emitEditNotes(assetItem) {
      GlobalBus.$emit(MODAL.PORTFOLIO_REVIEW_EDIT_NOTES, {
        asset: assetItem,
        onSave: this.handleSaveNotes,
      })
    },
    handleSaveNotes({ asset, comentario, setor, recomendacao, tags } = {}) {
      if (!asset) {
        return
      }
      const payload = this.buildNotesPayload({ asset, comentario, setor, recomendacao, tags })
      this.$emit('save-notes', payload)
    },
    buildNotesPayload({ asset, comentario, setor, recomendacao, tags } = {}) {
      const commentValue = typeof comentario === 'string' ? comentario : ''
      const setorValue = setor !== undefined ? setor : asset && asset.setor
      const tagsList = Array.isArray(tags) ? tags : []
      return {
        asset,
        comentario: commentValue,
        setor: setorValue || '',
        recomendacao: recomendacao !== undefined ? recomendacao : (asset && asset.recomendacao) || null,
        tags: tagsList,
      }
    },
    getTags(assetItem) {
      if (!assetItem || !Array.isArray(assetItem.tags)) {
        return []
      }
      return assetItem.tags.filter((tagValue) => {
        return typeof tagValue === 'string' && tagValue.trim()
      })
    },
    emitBlockAsset(assetItem) {
      this.$emit('block-asset', assetItem)
    },
    emitChangeAsset(assetItem) {
      this.$emit('change-asset', assetItem)
    },
    emitFixAsset(assetItem) {
      this.$emit('fix-asset', assetItem)
    },
    isAssetFixed(assetItem) {
      if (!assetItem || !Array.isArray(this.fixedAssets)) {
        return false
      }
      return this.fixedAssets.some((fixedItem) => {
        return fixedItem && fixedItem.nome_ativo === assetItem.nome_ativo
      })
    },
    buildRowActions(assetItem) {
      const isFixed = this.isAssetFixed(assetItem)
      const restricted = this.onlyEditNotes
      const fixLabel = isFixed ? 'Remover ativo dos fixados' : 'Fixar ativo'
      const fixTooltip = restricted ? ONLY_NOTES_TOOLTIP : fixLabel
      return [
        {
          key: 'edit-notes',
          icon: 'mdi-note-edit-outline',
          tooltip: 'Editar comentário e tags',
          handler: () => this.emitEditNotes(assetItem),
        },
        {
          key: 'block-asset',
          icon: 'mdi-cancel',
          tooltip: restricted
            ? ONLY_NOTES_TOOLTIP
            : 'Bloquear ativo na simulação (não sugerir a compra)',
          disabled: restricted,
          handler: () => this.emitBlockAsset(assetItem),
        },
        {
          key: 'change-asset',
          icon: 'mdi-shuffle-variant',
          tooltip: restricted ? ONLY_NOTES_TOOLTIP : 'Trocar ativo',
          disabled: restricted,
          handler: () => this.emitChangeAsset(assetItem),
        },
        {
          key: 'fix-asset',
          icon: isFixed ? 'mdi-pin-off-outline' : 'mdi-pin-outline',
          tooltip: fixTooltip,
          disabled: restricted,
          handler: () => this.emitFixAsset(assetItem),
        },
      ]
    },
  },
}
</script>

<template>
  <div class="simulator-portfolio-table">
    <v-data-table
      :headers="visibleHeaders"
      :items="tableRows"
      :group-by="groupBy"
      :custom-sort="customSort"
      class="table-new-ux-group-by-v1"
      hide-default-footer
      disable-pagination
    >
      <template v-slot:group.header="{ group, items: groupItems }">
        <td
          v-for="headerItem in visibleHeaders"
          :key="headerItem.value || headerItem.text"
          class="text-semibold"
        >
          <span v-if="headerItem.value === 'nome_ativo'">
            {{ group }}
          </span>
          <span
            v-else-if="
              headerItem.value === 'aplicacao' ||
              headerItem.value === 'Aplicação'
            "
          >
            {{ sumPl(groupItems) }}
          </span>
          <span v-else-if="headerItem.value === 'percent'">
            {{ sumPercent(groupItems) }}
          </span>
        </td>
      </template>

      <template v-slot:item.nome_ativo="{ item }">
        <div v-if="!isCompact">
          {{ item.nome_ativo || '—' }}
        </div>
        <v-hover v-else v-slot="{ hover }">
          <div class="simulator-portfolio-table__asset">
            <div class="d-flex align-center">
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <div
                    class="text-truncate simulator-portfolio-table__asset-name-compact"
                    v-bind="attrs"
                    v-on="on"
                  >
                    {{ item.nome_ativo || '—' }}
                  </div>
                </template>
                <div>{{ item.nome_ativo || '—' }}</div>
              </v-tooltip>

              <CommentDisplay
                class="ml-1"
                display-mode="icon"
                :comment-text="item && item.comentario"
                :has-extra-content="getTags(item).length > 0"
                :is-hovering="hover"
                empty-tooltip-label="Editar comentário e tags"
                @click="emitEditNotes(item)"
              />
            </div>

            <ClientTags
              v-if="getTags(item).length"
              class="simulator-portfolio-table__asset-tags"
              :tags="getTags(item)"
              useSmallChips
            />
          </div>
        </v-hover>
      </template>

      <template v-slot:item.referencia="{ item }">
        <div>
          {{ formatReference(item) }}
        </div>
      </template>

      <template v-slot:item.aplicacao="{ item }">
        <div style="white-space: nowrap">
          {{ item.aplicacao }}
        </div>
      </template>

      <template v-slot:item.recomendacao="{ item }">
        <v-chip
          v-if="item.recomendacao"
          x-small
          :color="recomendacaoColors[item.recomendacao] || 'grey'"
          dark
        >
          {{ recomendacaoLabels[item.recomendacao] || item.recomendacao }}
        </v-chip>
        <span v-else> - </span>
      </template>

      <template v-slot:item.setor="{ item }">
        <v-tooltip v-if="item.setor" top>
          <template v-slot:activator="{ on, attrs }">
            <div class="text-truncate" style="max-width: 120px" v-bind="attrs" v-on="on">
              {{ item.setor }}
            </div>
          </template>
          <span>{{ item.setor }}</span>
        </v-tooltip>
        <div v-else> - </div>
      </template>

      <template v-slot:item.comentario="{ item }">
        <CommentDisplay
          :comment-text="item && item.comentario"
          display-mode="text"
        />
      </template>

      <template v-slot:item.tags="{ item }">
        <ClientTags
          v-if="getTags(item).length"
          :tags="getTags(item)"
          :max-tags-display="maxTagsDisplay"
          useSmallChips
        />
        <div v-else> - </div>
      </template>

      <template v-slot:item.actions="{ item }">
        <TableRowActions :actions="item.actions" />
      </template>
    </v-data-table>
  </div>
</template>

<style lang="sass" scoped>
.simulator-portfolio-table
  &__asset-name-compact
    max-width: 425px

  &__asset-tags
    margin-bottom: 6px

</style>
